import { authApi } from './authApi';
import { validatePermissionContext, type PlatformAccessToken, type PlatformPermissionContext } from '@jukun/platform-sdk/auth';

type CachedToken = Awaited<ReturnType<typeof authApi.accessToken>>;
type PendingToken = {
  generation: number;
  promise: Promise<CachedToken>;
};

const tokens = new Map<string, CachedToken>();
const contexts = new WeakMap<PlatformAccessToken, PlatformPermissionContext>();
const versions = new Map<string, number>();
const listeners = new Set<(applicationCode: string) => void>();
const pending = new Map<string, PendingToken>();
const REFRESH_WINDOW_MS = 30_000;
let generation = 0;

export function permissionContextForToken(token: PlatformAccessToken): PlatformPermissionContext {
  const context = contexts.get(token);
  if (!context) throw new Error('目标应用权限快照未就绪');
  return context;
}

export function applicationPermissionCodes(applicationCode: string): readonly string[] {
  const token = tokens.get(applicationCode.trim().toUpperCase());
  return token ? permissionContextForToken(token).permissionCodes : [];
}

export function subscribeApplicationPermissions(listener: (applicationCode: string) => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

async function loadToken(code: string, signal?: AbortSignal): Promise<CachedToken> {
  const requestGeneration = generation;
  const version = (versions.get(code) ?? 0) + 1;
  versions.set(code, version);
  const token = await authApi.accessToken(code, signal);
  const context = validatePermissionContext(
    await authApi.permissionContext(code, signal), code, token,
  );
  if (signal?.aborted || requestGeneration !== generation) {
    throw new DOMException('Token request invalidated', 'AbortError');
  }
  contexts.set(token, context);
  // A concurrent host keeps its own valid ticket; only the newest request may publish shared state.
  if (versions.get(code) === version) {
    tokens.set(code, token);
    listeners.forEach(listener => listener(code));
  }
  return token;
}

export async function ensureAccessToken(
  applicationCode: string,
  force = false,
  signal?: AbortSignal,
) {
  const code = applicationCode.trim().toUpperCase();
  if (signal?.aborted) throw new DOMException('Token request aborted', 'AbortError');
  const current = tokens.get(code);
  if (!force && current
      && Date.parse(current.expiresAt) - Date.now() > REFRESH_WINDOW_MS) {
    return current;
  }
  if (signal) {
    return loadToken(code, signal);
  }
  const inflight = pending.get(code);
  if (inflight) return inflight.promise;
  const requestGeneration = generation;
  const promise = loadToken(code)
    .finally(() => {
      if (pending.get(code)?.promise === promise) pending.delete(code);
    });
  promise.catch(() => undefined);
  pending.set(code, { generation: requestGeneration, promise });
  return promise;
}

export function clearAccessTokens() {
  generation += 1;
  tokens.clear();
  pending.clear();
  versions.clear();
}
